// src/data/articles/how-to-write-a-funny-joke.js
// All language versions of the "How to Write a Funny Joke" article

export default [
  // ── How to Write a Funny Joke (English) ──────────────────────────────────
  {
    slug: 'how-to-write-a-funny-joke',
    lang: 'en',
    translations: { en: 'how-to-write-a-funny-joke', fr: 'comment-ecrire-une-blague', es: 'como-escribir-un-chiste', ar: 'kaifa-taktub-nuktatan' },
    title: 'How to Write a Funny Joke: A Step-by-Step Guide to Making People Laugh',
    seoTitle: 'How to Write a Funny Joke: A Step-by-Step Guide',
    seoDescription:
      'Learn how to write a funny joke using proven techniques. Discover setup-punchline structure, exaggeration, wordplay, and misdirection used by professional comedians.',
    publishedDate: '2026-03-01',
    readingMinutes: 8,
    relatedCategories: ['general', 'work', 'relationships', 'friends'],
    relatedArticles: ['how-to-be-naturally-funny', 'joke-structures', 'what-joke-would-make-a-child-laugh'],
    intro: [
      'Have you ever wondered how to write a funny joke that genuinely makes people laugh?',
      'Writing humor isn\'t just about being naturally funny. In reality, most great jokes follow specific structures and psychological principles. The good news? Humor is a skill you can learn and improve.',
      'In this guide, you\'ll discover the techniques professional comedians use and the exact steps to create jokes that actually work.',
    ],
    sections: [
      {
        number: 1,
        heading: 'Understand What Makes a Joke Funny',
        anchor: 'what-makes-it-funny',
        content: [
          {
            type: 'paragraph',
            text: 'Before learning how to write a good joke, you need to understand why people laugh.',
          },
          {
            type: 'lead',
            text: 'Most humor relies on three key elements:',
          },
          {
            type: 'list',
            items: [
              'Surprise – An unexpected twist',
              'Incongruity – A mismatch between expectation and reality',
              'Relatability – A shared human experience',
            ],
          },
          {
            type: 'paragraph',
            text: 'When the brain predicts one outcome and receives another, it triggers laughter. This is known as the incongruity theory of humor.',
          },
        ],
      },
      {
        number: 2,
        heading: 'Use the Classic Joke Structure (Setup + Punchline)',
        anchor: 'setup-punchline',
        content: [
          {
            type: 'paragraph',
            text: 'Nearly every joke follows this simple format:',
          },
          {
            type: 'lead',
            text: 'Setup',
          },
          {
            type: 'paragraph',
            text: 'Introduces a normal situation and builds expectation.',
          },
          {
            type: 'lead',
            text: 'Punchline',
          },
          {
            type: 'paragraph',
            text: 'Delivers an unexpected twist.',
          },
          {
            type: 'lead',
            text: 'Example structure:',
          },
          {
            type: 'list',
            items: [
              'Setup: I started a fitness routine last week.',
              'Punchline: So far, I\'ve only trained my excuses.',
            ],
          },
          {
            type: 'paragraph',
            text: 'The punchline works because it subverts the expectation created in the setup.',
          },
        ],
      },
      {
        number: 3,
        heading: 'Keep It Short and Clear',
        anchor: 'keep-it-short',
        content: [
          {
            type: 'lead',
            text: 'If you\'re learning how to make a joke funny, remember: shorter is usually better.',
          },
          {
            type: 'paragraph',
            text: 'Long explanations reduce impact. A strong joke:',
          },
          {
            type: 'list',
            items: [
              'Uses simple language',
              'Avoids unnecessary details',
              'Delivers the twist quickly',
            ],
          },
          {
            type: 'paragraph',
            text: 'Clarity strengthens the punchline.',
          },
        ],
      },
      {
        number: 4,
        heading: 'Use Exaggeration',
        anchor: 'use-exaggeration',
        content: [
          {
            type: 'paragraph',
            text: 'Exaggeration is one of the most powerful humor techniques. Turning something small into something absurd creates contrast.',
          },
          {
            type: 'lead',
            text: 'Example:',
          },
          {
            type: 'paragraph',
            text: 'I\'m not lazy. I\'m on energy-saving mode permanently.',
          },
          {
            type: 'paragraph',
            text: 'The exaggeration makes the situation ridiculous and relatable at the same time.',
          },
        ],
      },
      {
        number: 5,
        heading: 'Play With Word Twists and Double Meanings',
        anchor: 'wordplay',
        content: [
          {
            type: 'paragraph',
            text: 'Wordplay is effective because it tricks the brain.',
          },
          {
            type: 'lead',
            text: 'Techniques include:',
          },
          {
            type: 'list',
            items: [
              'Puns',
              'Double meanings',
              'Literal interpretations',
            ],
          },
          {
            type: 'lead',
            text: 'Example:',
          },
          {
            type: 'paragraph',
            text: 'I used to be a baker, but I couldn\'t make enough dough.',
          },
          {
            type: 'paragraph',
            text: 'The humor comes from the double meaning of "dough."',
          },
        ],
      },
      {
        number: 6,
        heading: 'Make It Relatable',
        anchor: 'make-it-relatable',
        content: [
          {
            type: 'paragraph',
            text: 'Relatable jokes perform better because people see themselves in them.',
          },
          {
            type: 'lead',
            text: 'Think about:',
          },
          {
            type: 'list',
            items: [
              'Work struggles',
              'School memories',
              'Family dynamics',
              'Technology frustrations',
              'Everyday awkward moments',
            ],
          },
          {
            type: 'paragraph',
            text: 'The more universal the experience, the stronger the laugh.',
          },
        ],
      },
      {
        number: 7,
        heading: 'Break Expectations Intentionally',
        anchor: 'break-expectations',
        content: [
          {
            type: 'paragraph',
            text: 'If you want to master how to write a funny joke, focus on misdirection.',
          },
          {
            type: 'paragraph',
            text: 'Guide the audience toward one logical ending… then flip it.',
          },
          {
            type: 'lead',
            text: 'Example:',
          },
          {
            type: 'list',
            items: [
              'I told my boss I needed a raise because three companies were after me.',
              'He asked which ones.',
              'I said: gas, electricity, and internet.',
            ],
          },
          {
            type: 'paragraph',
            text: 'The unexpected shift creates humor.',
          },
        ],
      },
      {
        number: 8,
        heading: 'Test and Refine Your Joke',
        anchor: 'test-and-refine',
        content: [
          {
            type: 'paragraph',
            text: 'Even professional comedians test jokes multiple times.',
          },
          {
            type: 'lead',
            text: 'To improve:',
          },
          {
            type: 'list',
            items: [
              'Try it on friends',
              'Watch their reaction',
              'Remove unnecessary words',
              'Strengthen the punchline',
            ],
          },
          {
            type: 'paragraph',
            text: 'Editing is where good jokes become great.',
          },
        ],
      },
      {
        number: 9,
        heading: 'How to Practice Writing Funny Jokes Daily',
        anchor: 'practice-daily',
        content: [
          {
            type: 'paragraph',
            text: 'Want to improve quickly? Try this daily exercise:',
          },
          {
            type: 'list',
            items: [
              'Write 5 random observations from your day',
              'Pick one ordinary situation',
              'Add exaggeration',
              'Create an unexpected ending',
            ],
          },
          {
            type: 'paragraph',
            text: 'The more you practice, the sharper your humor instincts become.',
          },
        ],
      },
      {
        number: 10,
        heading: 'Can Anyone Learn How to Write a Funny Joke?',
        anchor: 'can-anyone-learn',
        content: [
          {
            type: 'paragraph',
            text: 'Yes.',
          },
          {
            type: 'paragraph',
            text: 'While some people have natural comedic timing, humor is largely a learnable skill. By understanding structure, surprise, and relatability, anyone can improve.',
          },
          {
            type: 'paragraph',
            text: 'Consistency is more important than talent.',
          },
        ],
      },
    ],
    tips: {
      heading: 'Common Mistakes to Avoid When Writing Jokes',
      intro: 'If your joke isn\'t landing, you might be:',
      items: [
        'Explaining the punchline',
        'Making it too long',
        'Being too predictable',
        'Forcing the humor',
        'Copying instead of creating',
      ],
      outro: 'Subtlety and timing matter.',
    },
    faq: [
      {
        q: 'Is joke writing a skill or talent?',
        a: 'It\'s mostly a skill. Structure and practice matter more than natural talent.',
      },
      {
        q: 'How long should a joke be?',
        a: 'As short as possible while keeping clarity. Most good jokes can be told in two sentences.',
      },
      {
        q: 'Why do some jokes fail?',
        a: 'Because they lack surprise, clarity, or relatability. Explaining the punchline also kills the humor.',
      },
      {
        q: 'How can I improve my comedic timing?',
        a: 'Practice delivering jokes out loud and observe audience reactions. Record yourself to spot unnecessary pauses or filler words.',
      },
    ],
    conclusion: [
      'Learning how to write a funny joke is about understanding psychology, structure, and surprise.',
      'Keep your jokes short, relatable, and unexpected. Use exaggeration, wordplay, and misdirection. Most importantly, practice consistently.',
      'Humor is not magic — it\'s mechanics. And once you understand the mechanics, you can make almost anything funny.',
    ],
  },

  // ── Comment écrire une blague drôle (French) ─────────────────────────────
  {
    slug: 'comment-ecrire-une-blague',
    lang: 'fr',
    translations: { en: 'how-to-write-a-funny-joke', fr: 'comment-ecrire-une-blague', es: 'como-escribir-un-chiste', ar: 'kaifa-taktub-nuktatan' },
    title: 'Comment écrire une blague drôle : guide complet pour faire rire',
    seoTitle: 'Comment écrire une blague drôle : guide complet',
    seoDescription:
      'Apprenez à écrire une blague drôle en suivant les étapes des humoristes professionnels. Structure, exagération, jeu de mots : maîtrisez l\'art de faire rire.',
    publishedDate: '2026-03-01',
    readingMinutes: 8,
    relatedCategories: ['general', 'work', 'relationships', 'friends'],
    relatedArticles: ['comment-etre-naturellement-drole', 'structures-de-blague', 'quelle-blague-fait-rire-les-enfants'],
    intro: [
      'Vous avez déjà voulu déclencher des éclats de rire autour de vous, mais vous ne saviez pas comment faire une blague qui fonctionne vraiment ?',
      'Bonne nouvelle : écrire une blague drôle n\'est pas une question de talent inné. C\'est une compétence qui s\'apprend, comme écrire une histoire ou résoudre une équation. Les meilleurs humoristes ne sont pas nés drôles — ils ont étudié les mécanismes de l\'humour et les appliquent avec précision.',
      'Dans ce guide, vous découvrirez les techniques utilisées par les comédiens professionnels, les erreurs à éviter et les étapes concrètes pour créer des blagues qui font vraiment rire.',
    ],
    sections: [
      {
        number: 1,
        heading: 'Comprendre pourquoi une blague fait rire',
        anchor: 'pourquoi-une-blague-fait-rire',
        content: [
          {
            type: 'paragraph',
            text: 'Avant d\'apprendre à écrire une bonne blague, il faut comprendre pourquoi les gens rient.',
          },
          {
            type: 'lead',
            text: 'L\'humour repose sur trois éléments fondamentaux :',
          },
          {
            type: 'list',
            items: [
              'La surprise – une chute inattendue',
              'L\'incongruité – un décalage entre ce qu\'on attend et ce qui se passe',
              'La connivence – une expérience partagée avec le public',
            ],
          },
          {
            type: 'paragraph',
            text: 'Quand le cerveau anticipe une conclusion logique et reçoit une fin inattendue, il réagit par le rire. C\'est ce que les chercheurs appellent la théorie de l\'incongruité.',
          },
        ],
      },
      {
        number: 2,
        heading: 'Utiliser la structure classique (amorce + chute)',
        anchor: 'amorce-chute',
        content: [
          {
            type: 'paragraph',
            text: 'La quasi-totalité des blagues suit ce schéma simple :',
          },
          {
            type: 'lead',
            text: 'L\'amorce',
          },
          {
            type: 'paragraph',
            text: 'Elle présente une situation banale et crée une attente.',
          },
          {
            type: 'lead',
            text: 'La chute',
          },
          {
            type: 'paragraph',
            text: 'Elle délivre une surprise inattendue.',
          },
          {
            type: 'lead',
            text: 'Exemple :',
          },
          {
            type: 'list',
            items: [
              'Amorce : J\'ai commencé un programme sportif la semaine dernière.',
              'Chute : Pour l\'instant, j\'ai surtout entraîné mes excuses.',
            ],
          },
          {
            type: 'paragraph',
            text: 'La chute fonctionne parce qu\'elle renverse l\'attente créée par l\'amorce.',
          },
        ],
      },
      {
        number: 3,
        heading: 'Faire court et clair',
        anchor: 'faire-court-et-clair',
        content: [
          {
            type: 'lead',
            text: 'Pour savoir comment faire une blague qui fait vraiment rire, retenez une règle : plus c\'est court, mieux c\'est.',
          },
          {
            type: 'paragraph',
            text: 'Les explications longues tuent l\'effet comique. Une bonne blague :',
          },
          {
            type: 'list',
            items: [
              'Utilise un vocabulaire simple',
              'Évite les détails inutiles',
              'Délivre la chute rapidement',
            ],
          },
          {
            type: 'paragraph',
            text: 'La clarté renforce l\'impact de la chute.',
          },
        ],
      },
      {
        number: 4,
        heading: 'Exagérer pour créer le contraste',
        anchor: 'exageration',
        content: [
          {
            type: 'paragraph',
            text: 'L\'exagération est l\'une des techniques humoristiques les plus puissantes. Transformer quelque chose de banal en quelque chose d\'absurde crée un effet de contraste qui fait rire.',
          },
          {
            type: 'lead',
            text: 'Exemple :',
          },
          {
            type: 'paragraph',
            text: 'Je ne suis pas paresseux. Je suis simplement en mode économie d\'énergie permanente.',
          },
          {
            type: 'paragraph',
            text: 'L\'exagération rend la situation à la fois ridicule et terriblement reconnaissable.',
          },
        ],
      },
      {
        number: 5,
        heading: 'Jouer sur les mots et les doubles sens',
        anchor: 'jeux-de-mots',
        content: [
          {
            type: 'paragraph',
            text: 'Le jeu de mots est redoutable parce qu\'il court-circuite le cerveau.',
          },
          {
            type: 'lead',
            text: 'Les techniques les plus efficaces :',
          },
          {
            type: 'list',
            items: [
              'Le calembour',
              'Le double sens',
              'L\'interprétation au pied de la lettre',
            ],
          },
          {
            type: 'lead',
            text: 'Exemple :',
          },
          {
            type: 'paragraph',
            text: 'J\'ai arrêté de faire du pain — je ne gagnais pas assez de blé.',
          },
          {
            type: 'paragraph',
            text: 'L\'humour vient du double sens du mot « blé » (argent familier, mais aussi céréale).',
          },
        ],
      },
      {
        number: 6,
        heading: 'Créer la connivence avec le public',
        anchor: 'connivence-public',
        content: [
          {
            type: 'paragraph',
            text: 'Les blagues qui touchent le plus sont celles où le public se reconnaît.',
          },
          {
            type: 'lead',
            text: 'Exploitez des situations universelles comme :',
          },
          {
            type: 'list',
            items: [
              'Les galères au travail',
              'La vie de famille',
              'Les souvenirs d\'école',
              'Les frustrations avec la technologie',
              'Les moments gênants du quotidien',
            ],
          },
          {
            type: 'paragraph',
            text: 'Plus l\'expérience est partagée, plus le rire est fort.',
          },
        ],
      },
      {
        number: 7,
        heading: 'Tromper les attentes volontairement',
        anchor: 'tromper-attentes',
        content: [
          {
            type: 'paragraph',
            text: 'Maîtriser l\'art de faire rire, c\'est avant tout maîtriser la misdirection — conduire le public vers une conclusion logique… puis la retourner.',
          },
          {
            type: 'lead',
            text: 'Exemple :',
          },
          {
            type: 'list',
            items: [
              'J\'ai dit à mon chef que je voulais une augmentation parce que trois entreprises s\'intéressaient à moi.',
              'Il m\'a demandé lesquelles.',
              'J\'ai répondu : le gaz, l\'électricité et internet.',
            ],
          },
          {
            type: 'paragraph',
            text: 'Le glissement inattendu crée l\'effet comique.',
          },
        ],
      },
      {
        number: 8,
        heading: 'Tester et affiner votre blague',
        anchor: 'tester-affiner',
        content: [
          {
            type: 'paragraph',
            text: 'Même les grands humoristes testent leurs blagues de nombreuses fois avant de les sortir sur scène.',
          },
          {
            type: 'lead',
            text: 'Pour progresser :',
          },
          {
            type: 'list',
            items: [
              'Testez sur des proches',
              'Observez leurs réactions',
              'Supprimez les mots inutiles',
              'Renforcez la chute',
            ],
          },
          {
            type: 'paragraph',
            text: 'C\'est dans la retouche que les bonnes blagues deviennent excellentes.',
          },
        ],
      },
      {
        number: 9,
        heading: 'S\'entraîner à écrire des blagues au quotidien',
        anchor: 'entrainement-quotidien',
        content: [
          {
            type: 'paragraph',
            text: 'Vous voulez progresser rapidement ? Adoptez cet exercice quotidien :',
          },
          {
            type: 'list',
            items: [
              'Notez 5 observations banales de votre journée',
              'Choisissez une situation ordinaire',
              'Exagérez-la',
              'Trouvez une fin inattendue',
            ],
          },
          {
            type: 'paragraph',
            text: 'Plus vous pratiquez, plus votre instinct humoristique se développe.',
          },
        ],
      },
      {
        number: 10,
        heading: 'Est-ce que n\'importe qui peut apprendre à écrire des blagues ?',
        anchor: 'tout-le-monde-peut-apprendre',
        content: [
          {
            type: 'paragraph',
            text: 'Oui.',
          },
          {
            type: 'paragraph',
            text: 'Certaines personnes ont un sens du timing naturel, mais l\'humour est avant tout une compétence qui s\'acquiert. En comprenant la structure, la surprise et la connivence, chacun peut s\'améliorer.',
          },
          {
            type: 'paragraph',
            text: 'La régularité compte plus que le talent.',
          },
        ],
      },
    ],
    tips: {
      heading: 'Les erreurs à éviter quand on écrit une blague',
      intro: 'Si votre blague ne fait pas rire, vous faites peut-être l\'une de ces erreurs :',
      items: [
        'Expliquer la chute',
        'Faire trop long',
        'Être trop prévisible',
        'Forcer l\'humour',
        'Copier sans adapter',
      ],
      outro: 'La subtilité et le rythme font toute la différence.',
    },
    faq: [
      {
        q: 'L\'humour est-il un talent ou une compétence ?',
        a: 'Avant tout une compétence. La structure et la pratique comptent plus que le don naturel.',
      },
      {
        q: 'Quelle est la longueur idéale d\'une blague ?',
        a: 'La plus courte possible, à condition de rester clair. La plupart des bonnes blagues tiennent en deux phrases.',
      },
      {
        q: 'Pourquoi certaines blagues ne font-elles pas rire ?',
        a: 'Parce qu\'elles manquent de surprise, de clarté ou de connivence. Expliquer la chute la tue systématiquement.',
      },
      {
        q: 'Comment améliorer son timing comique ?',
        a: 'En s\'entraînant à dire des blagues à voix haute et en observant les réactions. S\'enregistrer aide à repérer les pauses inutiles.',
      },
    ],
    conclusion: [
      'Apprendre à écrire une blague drôle, c\'est comprendre la psychologie, la structure et l\'effet de surprise.',
      'Faites court, restez proche du vécu de votre public et renversez ses attentes. Utilisez l\'exagération, le jeu de mots et la misdirection. Surtout, pratiquez régulièrement.',
      'L\'humour n\'est pas de la magie — c\'est de la mécanique. Et une fois que vous en maîtrisez les mécanismes, vous pouvez rendre presque n\'importe quoi drôle.',
    ],
  },

  // ── Cómo escribir un chiste gracioso (Spanish) ───────────────────────────
  {
    slug: 'como-escribir-un-chiste',
    lang: 'es',
    translations: { en: 'how-to-write-a-funny-joke', fr: 'comment-ecrire-une-blague', es: 'como-escribir-un-chiste', ar: 'kaifa-taktub-nuktatan' },
    title: 'Cómo Escribir un Chiste Gracioso: Guía Paso a Paso para Hacer Reír',
    seoTitle: 'Cómo Escribir un Chiste Gracioso: Guía Paso a Paso',
    seoDescription:
      'Aprende a escribir chistes graciosos con técnicas probadas. Descubre la estructura setup-remate, la exageración y el juego de palabras que usan los comediantes profesionales.',
    publishedDate: '2026-03-01',
    readingMinutes: 8,
    relatedCategories: ['general', 'work', 'relationships', 'friends'],
    relatedArticles: ['como-ser-naturalmente-gracioso', 'estructuras-de-chiste', 'que-chiste-hace-reir-a-un-nino'],
    intro: [
      '¿Alguna vez te has preguntado cómo escribir un chiste gracioso que realmente haga reír a la gente?',
      'Escribir humor no es solo una cuestión de ser naturalmente divertido. En realidad, los mejores chistes siguen estructuras específicas y principios psicológicos. La buena noticia es que el humor es una habilidad que puedes aprender y mejorar.',
      'En esta guía, descubrirás las técnicas que usan los comediantes profesionales y los pasos exactos para crear chistes que realmente funcionen.',
    ],
    sections: [
      {
        number: 1,
        heading: 'Entender qué hace gracioso a un chiste',
        anchor: 'que-hace-gracioso-un-chiste',
        content: [
          {
            type: 'paragraph',
            text: 'Antes de aprender a escribir un buen chiste, necesitas entender por qué la gente se ríe.',
          },
          {
            type: 'lead',
            text: 'La mayoría del humor se basa en tres elementos clave:',
          },
          {
            type: 'list',
            items: [
              'Sorpresa – Un giro inesperado',
              'Incongruencia – Un choque entre la expectativa y la realidad',
              'Identificación – Una experiencia humana compartida',
            ],
          },
          {
            type: 'paragraph',
            text: 'Cuando el cerebro anticipa un resultado y recibe otro, provoca la risa. Esto se conoce como la teoría de la incongruencia del humor.',
          },
        ],
      },
      {
        number: 2,
        heading: 'Usar la estructura clásica del chiste (planteo + remate)',
        anchor: 'planteo-remate',
        content: [
          {
            type: 'paragraph',
            text: 'Casi todos los chistes siguen este formato sencillo:',
          },
          {
            type: 'lead',
            text: 'Planteo',
          },
          {
            type: 'paragraph',
            text: 'Presenta una situación normal y crea una expectativa.',
          },
          {
            type: 'lead',
            text: 'Remate',
          },
          {
            type: 'paragraph',
            text: 'Entrega el giro inesperado.',
          },
          {
            type: 'lead',
            text: 'Ejemplo:',
          },
          {
            type: 'list',
            items: [
              'Planteo: Empecé una rutina de ejercicios la semana pasada.',
              'Remate: Hasta ahora, solo he entrenado mis excusas.',
            ],
          },
          {
            type: 'paragraph',
            text: 'El remate funciona porque subvierte la expectativa creada en el planteo.',
          },
        ],
      },
      {
        number: 3,
        heading: 'Ser breve y claro',
        anchor: 'ser-breve-y-claro',
        content: [
          {
            type: 'lead',
            text: 'Si quieres saber cómo hacer un chiste gracioso, recuerda: cuanto más corto, mejor.',
          },
          {
            type: 'paragraph',
            text: 'Las explicaciones largas reducen el impacto. Un buen chiste:',
          },
          {
            type: 'list',
            items: [
              'Usa lenguaje sencillo',
              'Evita detalles innecesarios',
              'Entrega el giro rápidamente',
            ],
          },
          {
            type: 'paragraph',
            text: 'La claridad potencia el remate.',
          },
        ],
      },
      {
        number: 4,
        heading: 'Usar la exageración',
        anchor: 'usar-la-exageracion',
        content: [
          {
            type: 'paragraph',
            text: 'La exageración es una de las técnicas humorísticas más poderosas. Convertir algo pequeño en algo absurdo crea un contraste que hace reír.',
          },
          {
            type: 'lead',
            text: 'Ejemplo:',
          },
          {
            type: 'paragraph',
            text: 'No soy vago. Estoy en modo ahorro de energía permanente.',
          },
          {
            type: 'paragraph',
            text: 'La exageración hace que la situación sea ridícula y reconocible al mismo tiempo.',
          },
        ],
      },
      {
        number: 5,
        heading: 'Jugar con las palabras y los dobles sentidos',
        anchor: 'juego-de-palabras',
        content: [
          {
            type: 'paragraph',
            text: 'El juego de palabras es efectivo porque engaña al cerebro.',
          },
          {
            type: 'lead',
            text: 'Técnicas que funcionan:',
          },
          {
            type: 'list',
            items: [
              'Juegos de palabras y calambures',
              'Dobles sentidos',
              'Interpretaciones literales de expresiones figuradas',
            ],
          },
          {
            type: 'lead',
            text: 'Ejemplo:',
          },
          {
            type: 'paragraph',
            text: 'Era panadero, pero no me levantaba el pan.',
          },
          {
            type: 'paragraph',
            text: 'El humor viene del doble sentido de la expresión: el pan que no sube al hornear y el dinero que no alcanza.',
          },
        ],
      },
      {
        number: 6,
        heading: 'Hacerlo identificable',
        anchor: 'hacerlo-identificable',
        content: [
          {
            type: 'paragraph',
            text: 'Los chistes identificables funcionan mejor porque la gente se ve reflejada en ellos.',
          },
          {
            type: 'lead',
            text: 'Piensa en situaciones que todos conocen:',
          },
          {
            type: 'list',
            items: [
              'Problemas en el trabajo',
              'Recuerdos del colegio',
              'La dinámica familiar',
              'La frustración con la tecnología',
              'Los momentos incómodos del día a día',
            ],
          },
          {
            type: 'paragraph',
            text: 'Cuanto más universal sea la experiencia, más fuerte será la risa.',
          },
        ],
      },
      {
        number: 7,
        heading: 'Romper las expectativas intencionalmente',
        anchor: 'romper-expectativas',
        content: [
          {
            type: 'paragraph',
            text: 'Si quieres dominar cómo escribir un chiste gracioso, enfócate en la misdirección: lleva al público hacia un final lógico… y luego dale la vuelta.',
          },
          {
            type: 'lead',
            text: 'Ejemplo:',
          },
          {
            type: 'list',
            items: [
              'Le dije a mi jefe que necesitaba un aumento porque tres empresas me querían.',
              'Me preguntó cuáles.',
              'Le dije: el gas, la luz e internet.',
            ],
          },
          {
            type: 'paragraph',
            text: 'El cambio inesperado crea el efecto cómico.',
          },
        ],
      },
      {
        number: 8,
        heading: 'Probar y perfeccionar el chiste',
        anchor: 'probar-y-perfeccionar',
        content: [
          {
            type: 'paragraph',
            text: 'Incluso los comediantes profesionales prueban sus chistes muchas veces antes de subirlos al escenario.',
          },
          {
            type: 'lead',
            text: 'Para mejorar:',
          },
          {
            type: 'list',
            items: [
              'Pruébalo con amigos o familiares',
              'Observa su reacción',
              'Elimina palabras innecesarias',
              'Fortalece el remate',
            ],
          },
          {
            type: 'paragraph',
            text: 'La edición es donde los buenos chistes se vuelven excelentes.',
          },
        ],
      },
      {
        number: 9,
        heading: 'Practicar la escritura de chistes a diario',
        anchor: 'practicar-a-diario',
        content: [
          {
            type: 'paragraph',
            text: '¿Quieres mejorar rápidamente? Prueba este ejercicio diario:',
          },
          {
            type: 'list',
            items: [
              'Escribe 5 observaciones aleatorias de tu día',
              'Elige una situación ordinaria',
              'Añade exageración',
              'Crea un final inesperado',
            ],
          },
          {
            type: 'paragraph',
            text: 'Cuanto más practiques, más agudos se vuelven tus instintos humorísticos.',
          },
        ],
      },
      {
        number: 10,
        heading: '¿Puede cualquiera aprender a escribir chistes graciosos?',
        anchor: 'cualquiera-puede-aprender',
        content: [
          {
            type: 'paragraph',
            text: 'Sí.',
          },
          {
            type: 'paragraph',
            text: 'Aunque algunas personas tienen un sentido del ritmo cómico natural, el humor es en gran parte una habilidad que se aprende. Entendiendo la estructura, la sorpresa y la identificación, cualquiera puede mejorar.',
          },
          {
            type: 'paragraph',
            text: 'La constancia importa más que el talento.',
          },
        ],
      },
    ],
    tips: {
      heading: 'Errores comunes al escribir chistes',
      intro: 'Si tu chiste no hace gracia, puede que estés cometiendo alguno de estos errores:',
      items: [
        'Explicar el remate',
        'Hacerlo demasiado largo',
        'Ser demasiado predecible',
        'Forzar el humor',
        'Copiar en lugar de crear',
      ],
      outro: 'La sutileza y el ritmo marcan la diferencia.',
    },
    faq: [
      {
        q: '¿Escribir chistes es una habilidad o un talento?',
        a: 'Es sobre todo una habilidad. La estructura y la práctica importan más que el talento natural.',
      },
      {
        q: '¿Qué extensión debe tener un chiste?',
        a: 'Lo más corta posible manteniendo la claridad. La mayoría de los buenos chistes se cuentan en dos frases.',
      },
      {
        q: '¿Por qué fracasan algunos chistes?',
        a: 'Porque les falta sorpresa, claridad o identificación. Explicar el remate también mata el humor.',
      },
      {
        q: '¿Cómo puedo mejorar mi sentido del ritmo cómico?',
        a: 'Practicando contar chistes en voz alta y observando las reacciones. Grabarte ayuda a detectar pausas innecesarias.',
      },
    ],
    conclusion: [
      'Aprender a escribir un chiste gracioso consiste en entender la psicología, la estructura y la sorpresa.',
      'Mantén tus chistes cortos, identificables e inesperados. Usa la exageración, el juego de palabras y la misdirección. Sobre todo, practica con constancia.',
      'El humor no es magia, es mecánica. Y una vez que entiendes la mecánica, puedes hacer que casi cualquier cosa sea graciosa.',
    ],
  },

  // ── كيف تكتب نكتة مضحكة (Arabic) ────────────────────────────────────────
  {
    slug: 'kaifa-taktub-nuktatan',
    lang: 'ar',
    translations: { en: 'how-to-write-a-funny-joke', fr: 'comment-ecrire-une-blague', es: 'como-escribir-un-chiste', ar: 'kaifa-taktub-nuktatan' },
    title: 'كيف تكتب نكتة مضحكة: دليل خطوة بخطوة لتجعل الناس يضحكون',
    seoTitle: 'كيف تكتب نكتة مضحكة: دليل خطوة بخطوة',
    seoDescription:
      'تعلّم كيف تكتب نكتة مضحكة باتباع أساليب المحترفين. اكتشف بنية النكتة، والمبالغة، والتورية لتجعل الناس يضحكون.',
    publishedDate: '2026-03-01',
    readingMinutes: 8,
    relatedCategories: ['general', 'work', 'relationships', 'friends'],
    relatedArticles: ['kaifa-takun-mudhikan-bitabitak', 'haykal-an-nukta', 'ay-nuktah-tudhik-al-tifl'],
    intro: [
      'هل تساءلت يومًا كيف تكتب نكتة مضحكة تجعل الناس يضحكون فعلًا؟',
      'كتابة النكات ليست موهبةً فطريةً حكرًا على المختارين. في الواقع، معظم النكات الجيدة تتبع أنماطًا محددة ومبادئ نفسية واضحة. والبشرى أن الفكاهة مهارةٌ تُتعلَّم وتتحسّن مع الممارسة.',
      'في هذا الدليل، ستكتشف الأساليب التي يستخدمها الكوميديون المحترفون، والخطوات الدقيقة لكتابة نكات تُضحك حقًا.',
    ],
    sections: [
      {
        number: 1,
        heading: 'فهم ما يجعل النكتة مضحكة',
        anchor: 'ma-yajaal-an-nuktata-mudhika',
        content: [
          {
            type: 'paragraph',
            text: 'قبل أن تتعلم كيف تكتب نكتة جيدة، عليك أن تفهم لماذا يضحك الناس.',
          },
          {
            type: 'lead',
            text: 'يقوم معظم الفكاهة على ثلاثة عناصر أساسية:',
          },
          {
            type: 'list',
            items: [
              'المفاجأة – تحوّل غير متوقع',
              'التناقض – التفاوت بين ما نتوقعه وما يحدث فعلًا',
              'الاشتراك في التجربة – موقف إنساني مألوف للجميع',
            ],
          },
          {
            type: 'paragraph',
            text: 'حين يتوقع الدماغ نتيجةً معينة ويتلقى نقيضها، تندلع الضحكة. وهذا ما يُعرف في علم النفس بنظرية التناقض في الفكاهة.',
          },
        ],
      },
      {
        number: 2,
        heading: 'استخدام البنية الكلاسيكية للنكتة (المقدمة + الضربة)',
        anchor: 'muqaddima-wa-darba',
        content: [
          {
            type: 'paragraph',
            text: 'تتبع كل نكتة تقريبًا هذا الشكل البسيط:',
          },
          {
            type: 'lead',
            text: 'المقدمة',
          },
          {
            type: 'paragraph',
            text: 'تُقدّم موقفًا عاديًا وتبني التوقع.',
          },
          {
            type: 'lead',
            text: 'الضربة (الخاتمة)',
          },
          {
            type: 'paragraph',
            text: 'تُفاجئ بتحوّل غير متوقع.',
          },
          {
            type: 'lead',
            text: 'مثال:',
          },
          {
            type: 'list',
            items: [
              'المقدمة: بدأتُ برنامجًا رياضيًا الأسبوع الماضي.',
              'الضربة: حتى الآن، درّبتُ أعذاري فحسب.',
            ],
          },
          {
            type: 'paragraph',
            text: 'تنجح الضربة لأنها تقلب التوقع الذي بنته المقدمة.',
          },
        ],
      },
      {
        number: 3,
        heading: 'الإيجاز والوضوح',
        anchor: 'al-ijaz-wal-wuduh',
        content: [
          {
            type: 'lead',
            text: 'إن أردتَ أن تكتب نكتةً مضحكةً فعلًا، تذكّر قاعدةً ذهبية: كلما كانت أقصر كانت أجدر.',
          },
          {
            type: 'paragraph',
            text: 'الشرح الطويل يُفسد المفاجأة. النكتة الجيدة:',
          },
          {
            type: 'list',
            items: [
              'تستخدم لغةً بسيطة',
              'تتجنب التفاصيل الزائدة',
              'تُقدّم الضربة بسرعة',
            ],
          },
          {
            type: 'paragraph',
            text: 'الوضوح يُضاعف تأثير الخاتمة.',
          },
        ],
      },
      {
        number: 4,
        heading: 'استخدام المبالغة',
        anchor: 'al-mubalagha',
        content: [
          {
            type: 'paragraph',
            text: 'المبالغة من أقوى أدوات الكوميديا. تحويل الأمر البسيط إلى شيء سخيف يخلق تناقضًا مضحكًا.',
          },
          {
            type: 'lead',
            text: 'مثال:',
          },
          {
            type: 'paragraph',
            text: 'أنا لستُ كسولًا. أنا في وضع توفير الطاقة على الدوام.',
          },
          {
            type: 'paragraph',
            text: 'المبالغة تجعل الموقف سخيفًا ومألوفًا في آنٍ واحد.',
          },
        ],
      },
      {
        number: 5,
        heading: 'اللعب بالألفاظ والمعاني المزدوجة',
        anchor: 'at-tawriya-wal-laab-bil-alfaz',
        content: [
          {
            type: 'paragraph',
            text: 'التورية فعّالة لأنها تخدع الدماغ وتُفاجئه.',
          },
          {
            type: 'lead',
            text: 'من أبرز الأساليب:',
          },
          {
            type: 'list',
            items: [
              'التورية والجناس',
              'المعاني المزدوجة',
              'التفسير الحرفي للعبارات المجازية',
            ],
          },
          {
            type: 'lead',
            text: 'مثال:',
          },
          {
            type: 'paragraph',
            text: 'كنتُ أعمل خبّازًا، لكنني لم أكن أجني ما يكفي من العجين.',
          },
          {
            type: 'paragraph',
            text: 'الضحكة تنبع من المعنى المزدوج: العجين المُستخدَم في الخبز، والعجين كناية عن المال.',
          },
        ],
      },
      {
        number: 6,
        heading: 'جعل النكتة قريبة من تجربة الجمهور',
        anchor: 'al-qarib-min-at-tajriba',
        content: [
          {
            type: 'paragraph',
            text: 'النكات التي يشعر فيها الجمهور بأنهم يُعبَّر عنهم تؤدي أداءً أفضل دائمًا.',
          },
          {
            type: 'lead',
            text: 'فكّر في مواقف يشاركها الجميع:',
          },
          {
            type: 'list',
            items: [
              'متاعب العمل والزملاء',
              'ذكريات المدرسة',
              'ديناميكيات الأسرة',
              'إحباطات التكنولوجيا',
              'المواقف المحرجة اليومية',
            ],
          },
          {
            type: 'paragraph',
            text: 'كلما كانت التجربة أكثر شيوعًا، كان الضحك أشد.',
          },
        ],
      },
      {
        number: 7,
        heading: 'كسر التوقعات عمدًا',
        anchor: 'kasr-at-tawaquat',
        content: [
          {
            type: 'paragraph',
            text: 'إن أردتَ إتقان كتابة النكات المضحكة، فركّز على التضليل الأنيق: قُد الجمهور نحو نهايةٍ منطقية... ثم اقلبها.',
          },
          {
            type: 'lead',
            text: 'مثال:',
          },
          {
            type: 'list',
            items: [
              'أخبرتُ مديري أنني أحتاج إلى زيادة في الراتب لأن ثلاث جهات تسعى إليّ.',
              'سألني: أيّ جهات؟',
              'أجبتُه: الغاز والكهرباء والإنترنت.',
            ],
          },
          {
            type: 'paragraph',
            text: 'التحوّل غير المتوقع هو ما يُحدث المفاجأة.',
          },
        ],
      },
      {
        number: 8,
        heading: 'اختبار النكتة وتهذيبها',
        anchor: 'ikhtibaru-an-nukta',
        content: [
          {
            type: 'paragraph',
            text: 'حتى الكوميديون المحترفون يختبرون نكاتهم مرارًا قبل تقديمها على المسرح.',
          },
          {
            type: 'lead',
            text: 'للتطور:',
          },
          {
            type: 'list',
            items: [
              'جرّبها مع أصدقائك أو عائلتك',
              'راقب ردود أفعالهم',
              'احذف الكلمات الزائدة',
              'قوّ الضربة الخاتمة',
            ],
          },
          {
            type: 'paragraph',
            text: 'التهذيب هو ما يُحوّل النكتة الجيدة إلى رائعة.',
          },
        ],
      },
      {
        number: 9,
        heading: 'التدرّب على كتابة النكات يوميًا',
        anchor: 'at-tadrib-al-yawmi',
        content: [
          {
            type: 'paragraph',
            text: 'تريد التحسّن بسرعة؟ جرّب هذا التمرين اليومي:',
          },
          {
            type: 'list',
            items: [
              'دوّن 5 ملاحظات عشوائية من يومك',
              'اختر موقفًا عاديًا واحدًا',
              'أضف مبالغةً إليه',
              'ابتكر نهايةً غير متوقعة',
            ],
          },
          {
            type: 'paragraph',
            text: 'كلما مارستَ أكثر، صار حدسك الفكاهي أكثر حدّةً وتلقائية.',
          },
        ],
      },
      {
        number: 10,
        heading: 'هل يستطيع أي شخص تعلّم كتابة النكات؟',
        anchor: 'hal-yastati-ay-shakhsin',
        content: [
          {
            type: 'paragraph',
            text: 'نعم.',
          },
          {
            type: 'paragraph',
            text: 'وإن كان بعض الناس يمتلكون إحساسًا فطريًا بالتوقيت الكوميدي، فإن الفكاهة مهارةٌ يمكن تعلّمها في معظمها. بفهم البنية والمفاجأة والاشتراك في التجربة، يستطيع أي شخص أن يتحسّن.',
          },
          {
            type: 'paragraph',
            text: 'الاستمرارية أهم من الموهبة.',
          },
        ],
      },
    ],
    tips: {
      heading: 'الأخطاء الشائعة عند كتابة النكات',
      intro: 'إن كانت نكتتك لا تُضحك، فربما تقع في أحد هذه الأخطاء:',
      items: [
        'شرح الضربة الخاتمة',
        'الإطالة الزائدة',
        'القابلية للتنبؤ',
        'فرض الفكاهة قسرًا',
        'النسخ عوضًا عن الابتكار',
      ],
      outro: 'الخفّة والتوقيت هما سرّ كل نكتة ناجحة.',
    },
    faq: [
      {
        q: 'هل كتابة النكات موهبة أم مهارة؟',
        a: 'هي في معظمها مهارة. البنية والممارسة أهم من الموهبة الفطرية.',
      },
      {
        q: 'ما الطول المثالي للنكتة؟',
        a: 'أقصر ما يمكن مع الحفاظ على الوضوح. معظم النكات الجيدة لا تتجاوز جملتين.',
      },
      {
        q: 'لماذا تفشل بعض النكات؟',
        a: 'لأنها تفتقر إلى المفاجأة أو الوضوح أو الاشتراك في التجربة. شرح الضربة يقتل الفكاهة على الفور.',
      },
      {
        q: 'كيف أحسّن توقيتي الكوميدي؟',
        a: 'بالتدرّب على رواية النكات بصوتٍ عالٍ ومراقبة ردود الأفعال. تسجيل نفسك يساعدك على اكتشاف التوقفات والكلمات الزائدة.',
      },
    ],
    conclusion: [
      'تعلّم كتابة نكتةٍ مضحكة يعني فهم علم النفس والبنية وعنصر المفاجأة.',
      'اجعل نكاتك قصيرةً وقريبةً من تجارب جمهورك وغير متوقعة. استخدم المبالغة والتورية والتضليل الأنيق. والأهم من كل ذلك: مارس باستمرار.',
      'الفكاهة ليست سحرًا — إنها ميكانيكا. وحين تتقن هذه الميكانيكا، تستطيع أن تجعل أي شيءٍ تقريبًا مضحكًا.',
    ],
  },
];
